import { setupForDark } from '@theme';
import { useMediaQuery } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPermission } from '@auth';
import { BottomSheet, ContextMenu, ContextMenuItem } from '@basic';
import { UIPaths } from '@consts';
import { useAccountsService } from '@hooks';
import { GoalIcon, TaskIcon } from '@icons';
import { ModelsFactory, PermissionEntityType, PermissionType } from '@models';
import { useStore } from '@store';

function CreateEntitiesMenu({
  isOpen,
  onClose,
  position
}: {
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [createInProgress, setCreateInProgress] = useState(false);
  const { accountMe, getAccountMe } = useAccountsService();
  const { createGoal, createTask } = useStore((state) => ({
    createGoal: state.createGoal,
    createTask: state.createTask
  }));
  const isSmOrHigher = useMediaQuery('(max-width: 640px)');
  const isMobile = useMemo(() => {
    return isSmOrHigher;
  }, [isSmOrHigher]);

  useEffect(() => {
    if (!accountMe) {
      getAccountMe();
    }
  }, []);

  let goalCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.GOAL);
  }, [accountMe]);

  let taskCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.TASK);
  }, [accountMe]);

  const onCreateGoal = async () => {
    if (!goalCreatePermission || createInProgress) return;

    setCreateInProgress(true);
    createGoal(ModelsFactory.emptyGoalPatch())
      .then((id: string) => {
        router.push(UIPaths.goalEditor(id));
      })
      .finally(() => {
        setCreateInProgress(false);
        onClose();
      });
  };

  const onCreateTask = () => {
    if (!taskCreatePermission || createInProgress) return;

    setCreateInProgress(true);
    createTask({ title: '' })
      .then((entityId: string) => {
        router.push(UIPaths.taskEditor(entityId));
      })
      .finally(() => {
        setCreateInProgress(false);
        onClose();
      });
  };

  return (
    <>
      {isMobile && (
        <BottomSheet
          isOpen={isOpen}
          title={t('components.createEntitiesMenu.create')}
          onClose={onClose}
        >
          {!goalCreatePermission && !taskCreatePermission && (
            <div
              className={classNames(
                'text-center',
                'p-sm',
                'P2R',
                setupForDark('text-tertiary'),
                'dk:w-[300px]'
              )}
            >
              {t('components.createEntitiesMenu.cannotCreateForNow')}
            </div>
          )}
          {taskCreatePermission && (
            <ContextMenuItem
              displayMode='mobile'
              onClick={onCreateTask}
              disabled={createInProgress}
            >
              <div className='flex flex-row gap-md items-center'>
                <TaskIcon size={16} type='bold' />
                {t('components.createEntitiesMenu.createTask')}
              </div>
            </ContextMenuItem>
          )}
          {goalCreatePermission && (
            <ContextMenuItem
              displayMode='mobile'
              onClick={onCreateGoal}
              disabled={createInProgress}
            >
              <div className='flex flex-row gap-md items-center'>
                <GoalIcon size={16} type='bold' />
                {t('components.createEntitiesMenu.createGoal')}
              </div>
            </ContextMenuItem>
          )}
        </BottomSheet>
      )}
      {!isMobile && (
        <ContextMenu
          position={position}
          onClose={onClose}
          withBackground={false}
          withBackdrop={true}
        >
          {!goalCreatePermission && !taskCreatePermission && (
            <div
              className={classNames(
                'text-center',
                'p-sm',
                'P2R',
                setupForDark('text-tertiary'),
                'dk:w-[300px]'
              )}
            >
              {t('components.createEntitiesMenu.cannotCreateForNow')}
            </div>
          )}
          {taskCreatePermission && (
            <ContextMenuItem onClick={onCreateTask} disabled={createInProgress}>
              <div className='flex flex-row gap-md items-center'>
                <TaskIcon size={16} type='bold' />
                {t('components.createEntitiesMenu.createTask')}
              </div>
            </ContextMenuItem>
          )}
          {goalCreatePermission && (
            <ContextMenuItem onClick={onCreateGoal} disabled={createInProgress}>
              <div className='flex flex-row gap-md items-center'>
                <GoalIcon size={16} type='bold' />
                {t('components.createEntitiesMenu.createGoal')}
              </div>
            </ContextMenuItem>
          )}
        </ContextMenu>
      )}
    </>
  );
}

export default CreateEntitiesMenu;
